import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PackageStatusBadge } from "@/components/StatusBadge";
import { CheckCircle2, Loader2, PackageSearch, Search } from "lucide-react";

export default function WarehousePackagesList({ status, title, description, action }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/warehouse/packages`, { params: { status, search: search || undefined } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [status]);

  const onAction = async (pkg) => {
    if (!action) return;
    setBusyId(pkg.package_id);
    try {
      await action.fn(pkg);
      toast.success(action.successMsg || "Done");
      fetch();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid={`wh-packages-${(Array.isArray(status) ? status.join("-") : status).toLowerCase()}`} className="space-y-5">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Warehouse</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
        {description && <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">{description}</p>}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); fetch(); }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="wh-pkg-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by QR, package ID, store, receiver…"
            className="pl-9 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
          />
        </div>
        <Button type="submit" className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">Search</Button>
      </form>

      {loading ? (
        <div className="py-14 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-14 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <PackageSearch className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-base text-slate-700 dark:text-slate-300 mb-1">No packages here</div>
          <div className="text-xs text-slate-500">When packages reach this stage, they'll show up.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(pkg => (
            <div
              key={pkg.id}
              data-testid={`wh-pkg-${pkg.package_id}`}
              className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40 hover:bg-slate-100 hover:dark:bg-slate-900 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-mono-tech text-xs text-cyan-300 truncate">{pkg.qr_code_id}</div>
                  <div className="font-mono-tech text-[11px] text-slate-500 truncate">{pkg.package_id}</div>
                </div>
                <PackageStatusBadge status={pkg.status} />
              </div>
              <div className="font-display font-semibold text-slate-900 dark:text-white truncate">{pkg.package_name}</div>
              <div className="text-xs text-slate-600 dark:text-slate-400">{pkg.store_name}</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
                <div className="text-slate-200">{pkg.receiver_name}</div>
                <div className="text-slate-600 dark:text-slate-400 text-xs">{pkg.receiver_phone}</div>
                <div className="sm:col-span-2 text-slate-600 dark:text-slate-400 text-xs truncate">{pkg.receiver_address}</div>
              </div>
              {pkg.assigned_rider_name && (
                <div className="mt-2 text-xs text-slate-500">First-Mile Rider: <span className="text-slate-700 dark:text-slate-300">{pkg.assigned_rider_name}</span></div>
              )}
              {pkg.outbound_batch_code && (
                <div className="mt-2 text-xs text-slate-500">Batch: <span className="text-sky-300 font-mono-tech">{pkg.outbound_batch_code}</span></div>
              )}
              {action && (
                <div className="mt-3">
                  <Button
                    data-testid={`wh-action-${action.testKey}-${pkg.package_id}`}
                    onClick={() => onAction(pkg)}
                    disabled={busyId === pkg.package_id}
                    className={action.className || "bg-cyan-400 text-slate-950 hover:bg-cyan-300"}
                  >
                    {busyId === pkg.package_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : action.icon ? <action.icon className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {action.label}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
